import { Routes } from '@angular/router';
import { HomeComponent } from './pages/home/home.component';
import { LoginComponent } from './pages/login/login.component';
import { UnauthorizedComponent } from './pages/unauthorized/unauthorized.component';
import { SettingsComponent } from './pages/settings/settings.component';
import { TenantSelectionComponent } from './pages/tenant-selection/tenant-selection.component';
import { NotFoundComponent } from '@crowdctrl/shared';
import { authGuard } from './guards/auth.guard';
import { downloadPageGuard } from './guards/download-page.guard';
import { queueFormsGuard } from './guards/queue-forms.guard';
import { orderDetailGuard } from './guards/order-detail.guard';
import { joinedSessionGuard } from './guards/joined-session.guard';
import { policySyncPendingGuard } from './guards/policy-sync-pending.guard';
import { createWorkspaceUnsavedGuard } from './guards/create-workspace-unsaved.guard';
import { worklistSettingsUnsavedGuard } from './guards/ionic-unsaved-changes.guard';

export const routes: Routes = [
  {
    path: 'login',
    component: LoginComponent,
  },
  {
    path: 'unauthorized',
    component: UnauthorizedComponent,
  },
  {
    path: 'about/technical',
    loadComponent: () => import('@crowdctrl/shared').then((m) => m.AboutTechnicalComponent),
  },
  {
    path: 'about/features',
    loadComponent: () => import('@crowdctrl/shared').then((m) => m.AboutFeaturesComponent),
  },
  {
    path: 'about/how-it-works/manager',
    loadComponent: () => import('@crowdctrl/shared').then((m) => m.AboutHowItWorksManagerComponent),
  },
  {
    path: 'about/how-it-works/service',
    loadComponent: () => import('@crowdctrl/shared').then((m) => m.AboutHowItWorksServiceComponent),
  },
  {
    path: 'about/how-it-works/forms',
    loadComponent: () => import('@crowdctrl/shared').then((m) => m.AboutHowItWorksFormsComponent),
  },
  {
    path: 'about/how-it-works/boards',
    loadComponent: () => import('@crowdctrl/shared').then((m) => m.AboutHowItWorksBoardsComponent),
  },
  {
    path: 'about/how-it-works',
    loadComponent: () => import('@crowdctrl/shared').then((m) => m.AboutHowItWorksComponent),
  },
  {
    path: 'about/future-directions',
    loadComponent: () => import('@crowdctrl/shared').then((m) => m.AboutFutureDirectionsComponent),
  },
  {
    path: 'register-interest',
    loadComponent: () => import('@crowdctrl/shared').then((m) => m.RegisterInterestShellComponent),
    children: [
      {
        path: '',
        loadComponent: () => import('@crowdctrl/shared').then((m) => m.RegisterInterestComponent),
      },
      {
        path: 'thanks',
        loadComponent: () =>
          import('@crowdctrl/shared').then((m) => m.RegisterInterestThanksComponent),
      },
    ],
  },
  {
    path: 'contact',
    loadComponent: () => import('@crowdctrl/shared').then((m) => m.ContactShellComponent),
    children: [
      {
        path: '',
        loadComponent: () => import('@crowdctrl/shared').then((m) => m.ContactComponent),
      },
      {
        path: 'sent',
        loadComponent: () => import('@crowdctrl/shared').then((m) => m.ContactSentComponent),
      },
    ],
  },
  {
    path: 'privacy-policy',
    loadComponent: () => import('@crowdctrl/shared').then((m) => m.PrivacyPolicyComponent),
  },
  {
    path: 'terms',
    loadComponent: () => import('@crowdctrl/shared').then((m) => m.TermsComponent),
  },
  {
    path: 'about',
    loadComponent: () => import('@crowdctrl/shared').then((m) => m.AboutProjectComponent),
  },
  {
    path: '',
    component: HomeComponent,
    canActivate: [authGuard],
  },
  {
    path: 'settings',
    component: SettingsComponent,
    canActivate: [authGuard],
  },
  {
    path: 'download',
    loadComponent: () =>
      import('./pages/download/download.component').then((m) => m.DownloadComponent),
    canActivate: [downloadPageGuard, authGuard],
  },
  {
    path: 'settings/developer',
    loadComponent: () =>
      import('./pages/settings/developer-settings/developer-settings.component').then(
        (m) => m.DeveloperSettingsComponent,
      ),
    canActivate: [authGuard],
  },
  {
    path: 'settings/theme',
    loadComponent: () =>
      import('./pages/settings/theme-settings/theme-settings.component').then(
        (m) => m.ThemeSettingsComponent,
      ),
    canActivate: [authGuard],
  },
  {
    path: 'sessions',
    loadComponent: () =>
      import('./pages/sessions/my-sessions.component').then((m) => m.MySessionsComponent),
    canActivate: [authGuard],
  },
  {
    path: 'workspaces/my',
    loadComponent: () =>
      import('./pages/workspaces/my-workspaces.component').then((m) => m.MyWorkspacesComponent),
    canActivate: [authGuard],
  },
  {
    path: 'tenant-selection',
    component: TenantSelectionComponent,
    canActivate: [authGuard],
  },
  {
    path: 'workspaces/bookmarks',
    redirectTo: '/workspaces',
    pathMatch: 'full',
  },
  {
    path: 'workspaces',
    loadComponent: () =>
      import('./pages/workspaces/workspaces.component').then((m) => m.WorkspacesComponent),
    canActivate: [authGuard],
  },
  {
    path: 'workspaces/create',
    loadComponent: () =>
      import('./pages/workspaces/create-workspace/create-workspace.component').then(
        (m) => m.CreateWorkspaceComponent,
      ),
    canActivate: [authGuard],
    canDeactivate: [createWorkspaceUnsavedGuard],
  },
  {
    path: 'workspaces/:id',
    loadComponent: () =>
      import('./pages/workspaces/workspace-tabs/workspace-tabs.component').then(
        (m) => m.WorkspaceTabsComponent,
      ),
    canActivate: [authGuard],
    children: [
      {
        path: '',
        redirectTo: 'sessions',
        pathMatch: 'full',
      },
      {
        path: 'overview',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/overview/overview.component').then(
            (m) => m.OverviewComponent,
          ),
      },
      {
        path: 'overview/order-counts/:status',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/overview/order-counts-by-status/order-counts-by-status.component').then(
            (m) => m.OrderCountsByStatusComponent,
          ),
      },
      {
        path: 'overview/wait-time',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/overview/average-time-by-session/average-time-by-session.component').then(
            (m) => m.AverageTimeBySessionComponent,
          ),
        data: { metric: 'waitTime' },
      },
      {
        path: 'overview/wait-time/distribution',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/overview/time-distribution/time-distribution.component').then(
            (m) => m.TimeDistributionComponent,
          ),
        data: { metric: 'waitTime' },
      },
      {
        path: 'overview/wait-time/:sessionId',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/overview/time-detail/time-detail.component').then(
            (m) => m.SessionOverviewTimeDetailComponent,
          ),
        data: { metric: 'waitTime' },
      },
      {
        path: 'overview/service-time',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/overview/average-time-by-session/average-time-by-session.component').then(
            (m) => m.AverageTimeBySessionComponent,
          ),
        data: { metric: 'serviceTime' },
      },
      {
        path: 'overview/service-time/distribution',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/overview/time-distribution/time-distribution.component').then(
            (m) => m.TimeDistributionComponent,
          ),
        data: { metric: 'serviceTime' },
      },
      {
        path: 'overview/service-time/:sessionId',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/overview/time-detail/time-detail.component').then(
            (m) => m.SessionOverviewTimeDetailComponent,
          ),
        data: { metric: 'serviceTime' },
      },
      {
        path: 'overview/turnaround-time',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/overview/average-time-by-session/average-time-by-session.component').then(
            (m) => m.AverageTimeBySessionComponent,
          ),
        data: { metric: 'turnaroundTime' },
      },
      {
        path: 'overview/turnaround-time/distribution',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/overview/time-distribution/time-distribution.component').then(
            (m) => m.TimeDistributionComponent,
          ),
        data: { metric: 'turnaroundTime' },
      },
      {
        path: 'overview/turnaround-time/:sessionId',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/overview/time-detail/time-detail.component').then(
            (m) => m.SessionOverviewTimeDetailComponent,
          ),
        data: { metric: 'turnaroundTime' },
      },
      {
        path: 'configuration',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/configuration/configuration.component').then(
            (m) => m.WorkspaceConfigurationComponent,
          ),
      },
      {
        path: 'configuration/queues/new',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/configuration/queues/queue-editor/queue-editor.component').then(
            (m) => m.QueueEditorComponent,
          ),
      },
      {
        path: 'configuration/queues/:queueId',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/configuration/queues/queue-detail/queue-detail.component').then(
            (m) => m.QueueDetailComponent,
          ),
      },
      {
        path: 'sessions/create',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/create-session/create-session.component').then(
            (m) => m.CreateSessionComponent,
          ),
      },
      {
        path: 'sessions/:sessionId/edit',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/edit-session/edit-session.component').then(
            (m) => m.EditSessionComponent,
          ),
      },
      {
        path: 'sessions',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/sessions.component').then(
            (m) => m.SessionsComponent,
          ),
      },
      {
        path: 'members',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/members/members.component').then(
            (m) => m.MembersComponent,
          ),
      },
      {
        path: 'members/create',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/members/add-member/select-user/select-user.component').then(
            (m) => m.SelectUserComponent,
          ),
      },
      {
        path: 'members/create/configure',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/members/add-member/configure-member/configure-member.component').then(
            (m) => m.ConfigureMemberComponent,
          ),
      },
      {
        path: 'members/:memberId',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/members/member-detail/member-detail.component').then(
            (m) => m.MemberDetailComponent,
          ),
      },
      {
        path: 'members/:memberId/change-role',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/members/change-role/change-role.component').then(
            (m) => m.ChangeRoleComponent,
          ),
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/settings/settings.component').then(
            (m) => m.WorkspaceSettingsComponent,
          ),
      },
      {
        path: 'settings/edit',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/settings/edit-workspace/edit-workspace.component').then(
            (m) => m.EditWorkspaceComponent,
          ),
      },
      {
        path: 'settings/manage-data',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/settings/manage-data/manage-data.component').then(
            (m) => m.WorkspaceManageDataComponent,
          ),
      },
      {
        path: 'settings/canvas-lms-integration',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/settings/canvas-lms-integration/canvas-lms-integration.component').then(
            (m) => m.WorkspaceCanvasLmsIntegrationComponent,
          ),
      },
      {
        path: 'settings/access-overrides',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/settings/access-overrides/access-overrides.component').then(
            (m) => m.WorkspaceAccessOverridesComponent,
          ),
      },
    ],
  },
  {
    path: 'workspaces/:id/sessions/:sessionId',
    loadComponent: () =>
      import('./pages/workspaces/workspace-tabs/sessions/session-tabs/session-tabs.component').then(
        (m) => m.SessionTabsComponent,
      ),
    canActivate: [authGuard],
    children: [
      {
        path: '',
        redirectTo: 'overview',
        pathMatch: 'full',
      },
      {
        path: 'overview',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/overview/overview.component').then(
            (m) => m.SessionOverviewComponent,
          ),
      },
      {
        path: 'overview/wait-time',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/overview/time-detail/time-detail.component').then(
            (m) => m.SessionOverviewTimeDetailComponent,
          ),
        data: { metric: 'waitTime' },
      },
      {
        path: 'overview/wait-time/distribution',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/overview/time-distribution/time-distribution.component').then(
            (m) => m.TimeDistributionComponent,
          ),
        data: { metric: 'waitTime' },
      },
      {
        path: 'overview/service-time',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/overview/time-detail/time-detail.component').then(
            (m) => m.SessionOverviewTimeDetailComponent,
          ),
        data: { metric: 'serviceTime' },
      },
      {
        path: 'overview/service-time/distribution',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/overview/time-distribution/time-distribution.component').then(
            (m) => m.TimeDistributionComponent,
          ),
        data: { metric: 'serviceTime' },
      },
      {
        path: 'overview/turnaround-time',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/overview/time-detail/time-detail.component').then(
            (m) => m.SessionOverviewTimeDetailComponent,
          ),
        data: { metric: 'turnaroundTime' },
      },
      {
        path: 'overview/turnaround-time/distribution',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/overview/time-distribution/time-distribution.component').then(
            (m) => m.TimeDistributionComponent,
          ),
        data: { metric: 'turnaroundTime' },
      },
      {
        path: 'my-orders/join',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/my-orders/join-session/join-session.component').then(
            (m) => m.JoinSessionComponent,
          ),
      },
      {
        path: 'my-orders/policy-sync',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/my-orders/policy-sync/policy-sync.component').then(
            (m) => m.PolicySyncComponent,
          ),
        canActivate: [joinedSessionGuard],
      },
      {
        path: 'my-orders',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/my-orders/my-orders.component').then(
            (m) => m.MyOrdersComponent,
          ),
        canActivate: [joinedSessionGuard, policySyncPendingGuard],
      },
      {
        path: 'my-orders/search',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/search/search.component').then(
            (m) => m.SessionSearchComponent,
          ),
        canActivate: [joinedSessionGuard, policySyncPendingGuard],
      },
      {
        path: 'my-orders/worklist-settings',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/my-orders/worklist-settings/worklist-settings.component').then(
            (m) => m.WorklistSettingsComponent,
          ),
        canActivate: [joinedSessionGuard, policySyncPendingGuard],
        canDeactivate: [worklistSettingsUnsavedGuard],
      },
      {
        path: 'my-orders/serving-policy',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/my-orders/custom-serving-policy/custom-serving-policy.component').then(
            (m) => m.CustomServingPolicyComponent,
          ),
        canActivate: [joinedSessionGuard, policySyncPendingGuard],
      },
      {
        path: 'my-orders/order/:orderId',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/my-orders/order-detail/order-detail.component').then(
            (m) => m.OrderDetailComponent,
          ),
        canActivate: [joinedSessionGuard, policySyncPendingGuard, orderDetailGuard],
      },
      {
        path: 'my-orders/search/order/:orderId',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/my-orders/order-detail/order-detail.component').then(
            (m) => m.OrderDetailComponent,
          ),
        canActivate: [joinedSessionGuard, policySyncPendingGuard, orderDetailGuard],
      },
      {
        path: 'my-orders/order/:orderId/receipt',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/my-orders/order-detail/order-receipt/order-receipt.component').then(
            (m) => m.OrderReceiptComponent,
          ),
        canActivate: [joinedSessionGuard, policySyncPendingGuard, orderDetailGuard],
      },
      {
        path: 'my-orders/search/order/:orderId/receipt',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/my-orders/order-detail/order-receipt/order-receipt.component').then(
            (m) => m.OrderReceiptComponent,
          ),
        canActivate: [joinedSessionGuard, policySyncPendingGuard, orderDetailGuard],
      },
      {
        path: 'my-orders/order/:orderId/cancel',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/my-orders/order-detail/cancel-order/cancel-order.component').then(
            (m) => m.CancelOrderComponent,
          ),
        canActivate: [joinedSessionGuard, policySyncPendingGuard, orderDetailGuard],
      },
      {
        path: 'my-orders/search/order/:orderId/cancel',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/my-orders/order-detail/cancel-order/cancel-order.component').then(
            (m) => m.CancelOrderComponent,
          ),
        canActivate: [joinedSessionGuard, policySyncPendingGuard, orderDetailGuard],
      },
      {
        path: 'my-orders/order/:orderId/move',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/my-orders/order-detail/move-order/move-order.component').then(
            (m) => m.MoveOrderComponent,
          ),
        canActivate: [joinedSessionGuard, policySyncPendingGuard, orderDetailGuard],
      },
      {
        path: 'my-orders/search/order/:orderId/move',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/my-orders/order-detail/move-order/move-order.component').then(
            (m) => m.MoveOrderComponent,
          ),
        canActivate: [joinedSessionGuard, policySyncPendingGuard, orderDetailGuard],
      },
      {
        path: 'my-orders/queue-forms',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/my-orders/queue-form-list/queue-form-list.component').then(
            (m) => m.QueueFormListComponent,
          ),
        canActivate: [joinedSessionGuard, policySyncPendingGuard, queueFormsGuard],
      },
      {
        path: 'my-orders/queue-form/:formId',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/my-orders/queue-form/queue-form.component').then(
            (m) => m.QueueFormComponent,
          ),
        canActivate: [joinedSessionGuard, policySyncPendingGuard],
      },
      {
        path: 'my-orders/order-placed',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/my-orders/order-placed/order-placed.component').then(
            (m) => m.OrderPlacedPageComponent,
          ),
        canActivate: [joinedSessionGuard, policySyncPendingGuard],
      },
      {
        path: 'queues',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/queues/queues.component').then(
            (m) => m.SessionQueuesComponent,
          ),
      },
      {
        path: 'manual-intake',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/queues/manual-intake/manual-intake.component').then(
            (m) => m.ManualIntakeComponent,
          ),
      },
      {
        path: 'alerts',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/alerts/alerts.component').then(
            (m) => m.SessionAlertsComponent,
          ),
      },
      {
        path: 'alerts/create',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/alerts/create-alert/create-alert.component').then(
            (m) => m.CreateAlertComponent,
          ),
      },
      {
        path: 'alerts/:alertId/edit',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/alerts/create-alert/create-alert.component').then(
            (m) => m.CreateAlertComponent,
          ),
      },
      {
        path: 'activity',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/activity/activity.component').then(
            (m) => m.SessionActivityComponent,
          ),
      },
      {
        path: 'search',
        redirectTo: 'my-orders/search',
        pathMatch: 'full',
      },
      {
        path: 'controls',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/controls/controls.component').then(
            (m) => m.SessionControlsComponent,
          ),
      },
      {
        path: 'controls/formControls/:formId',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/controls/session-form-edit/session-form-edit.component').then(
            (m) => m.SessionFormEditComponent,
          ),
      },
      {
        path: 'controls/formControls/:formId/nfc-tags/create',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/controls/form-nfc-tag-create/form-nfc-tag-create.component').then(
            (m) => m.FormNfcTagCreateComponent,
          ),
      },
      {
        path: 'controls/formControls/:formId/nfc-tags',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/controls/form-nfc-tags/form-nfc-tags.component').then(
            (m) => m.FormNfcTagsComponent,
          ),
      },
      {
        path: 'controls/formControls/:formId/qr-codes/create',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/controls/form-qr-code-create/form-qr-code-create.component').then(
            (m) => m.FormQrCodeCreateComponent,
          ),
      },
      {
        path: 'controls/formControls/:formId/qr-codes/:qrCodeId',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/controls/form-qr-code-detail/form-qr-code-detail.component').then(
            (m) => m.FormQrCodeDetailComponent,
          ),
      },
      {
        path: 'controls/formControls/:formId/qr-codes',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/controls/form-qr-codes/form-qr-codes.component').then(
            (m) => m.FormQrCodesComponent,
          ),
      },
      {
        path: 'controls/formControls/:formId/nfc-write',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/controls/nfc-write/nfc-write.component').then(
            (m) => m.NfcWriteComponent,
          ),
      },
      {
        path: 'controls/queueControls/new',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/controls/session-queue-create/session-queue-create.component').then(
            (m) => m.SessionQueueCreateComponent,
          ),
      },
      {
        path: 'controls/queueControls/:queueId',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/controls/session-queue-edit/session-queue-edit.component').then(
            (m) => m.SessionQueueEditComponent,
          ),
      },
      {
        path: 'controls/boardControls/:boardId',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/controls/session-board-edit/session-board-edit.component').then(
            (m) => m.SessionBoardEditComponent,
          ),
      },
      {
        path: 'people',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/people/people.component').then(
            (m) => m.SessionPeopleComponent,
          ),
      },
      {
        path: 'people/create',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/people/add-member/select-workspace-member/select-workspace-member.component').then(
            (m) => m.SelectWorkspaceMemberComponent,
          ),
      },
      {
        path: 'people/create/configure',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/people/add-member/configure-session-member/configure-session-member.component').then(
            (m) => m.ConfigureSessionMemberComponent,
          ),
      },
      {
        path: 'people/:memberId',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/people/member-detail/member-detail.component').then(
            (m) => m.SessionMemberDetailComponent,
          ),
      },
      {
        path: 'people/:memberId/change-role',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/people/change-role/change-role.component').then(
            (m) => m.SessionChangeRoleComponent,
          ),
      },
      {
        path: 'settings',
        loadComponent: () =>
          import('./pages/workspaces/workspace-tabs/sessions/session-tabs/settings/settings.component').then(
            (m) => m.SessionSettingsComponent,
          ),
      },
    ],
  },
  {
    path: '**',
    component: NotFoundComponent,
  },
];
